import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/client';
import StarRating from '../../components/StarRating';
import { useToast } from '../../context/ToastContext';

export default function AdminTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/tickets/${id}`);
    setTicket(data.ticket);
    setComments(data.comments || []);
    setStatus(data.ticket.status);
    setPriority(data.ticket.priority);
    setAdminNotes(data.ticket.admin_notes || '');
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (e) {
        toast.error('Ticket not found');
        navigate('/admin/tickets');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const saveMeta = async (fieldUpdates) => {
    setSaving(true);
    try {
      await api.put(`/tickets/${id}`, fieldUpdates);
      toast.success('Ticket updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = async (e) => {
    const v = e.target.value;
    setStatus(v);
    await saveMeta({ status: v });
  };

  const onPriorityChange = async (e) => {
    const v = e.target.value;
    setPriority(v);
    await saveMeta({ priority: v });
  };

  const saveNotes = async () => {
    await saveMeta({ admin_notes: adminNotes });
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/tickets/${id}/comments`, { comment: note, is_internal: true });
      toast.success('Note added');
      setNote('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !ticket) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="tr-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        <Link to="/admin/tickets">Tickets</Link>
        <span> / </span>
        <span>{ticket.ticket_number}</span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: '1.25rem',
          alignItems: 'start',
        }}
      >
        <div className="tr-card" style={{ padding: '1.25rem' }}>
          <h1 style={{ marginTop: 0, fontSize: '1.35rem' }}>{ticket.subject}</h1>
          <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />
          <h3 style={{ fontSize: '1rem' }}>Visitor</h3>
          <p style={{ margin: '0.25rem 0' }}>{ticket.visitor_name}</p>
          {ticket.visitor_email && <p style={{ margin: '0.25rem 0' }}>{ticket.visitor_email}</p>}
          {ticket.visitor_phone && <p style={{ margin: '0.25rem 0' }}>{ticket.visitor_phone}</p>}
          <h3 style={{ fontSize: '1rem' }}>Location</h3>
          <p>
            {ticket.location_name} — {ticket.floor}, {ticket.building}
          </p>
          <h3 style={{ fontSize: '1rem' }}>Category</h3>
          <p>{ticket.category}</p>
          <h3 style={{ fontSize: '1rem' }}>Rating</h3>
          <StarRating value={ticket.rating} readOnly />
        </div>

        <div className="tr-card" style={{ padding: '1.25rem' }}>
          <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Update ticket</h2>
          <label className="tr-label">Status</label>
          <select className="tr-input" value={status} onChange={onStatusChange} disabled={saving}>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <label className="tr-label" style={{ marginTop: '1rem' }}>
            Priority
          </label>
          <select className="tr-input" value={priority} onChange={onPriorityChange} disabled={saving}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <label className="tr-label" style={{ marginTop: '1rem' }}>
            Admin notes
          </label>
          <textarea className="tr-input" rows={4} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
          <button type="button" className="tr-btn tr-btn-primary" style={{ marginTop: '0.75rem' }} onClick={saveNotes} disabled={saving}>
            Save notes
          </button>
        </div>
      </div>

      <div className="tr-card" style={{ padding: '1.25rem', marginTop: '1.25rem' }}>
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Activity &amp; internal notes</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          {comments.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No notes yet.</p>}
          {comments.map((c) => (
            <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', background: 'var(--bg-muted)' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {c.user_name} · {new Date(c.created_at).toLocaleString()}
                {c.is_internal ? ' · Internal' : ''}
              </div>
              <div style={{ marginTop: '0.35rem' }}>{c.comment}</div>
            </div>
          ))}
        </div>
        <form onSubmit={addComment}>
          <label className="tr-label">Add internal note</label>
          <textarea className="tr-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Visible to admins only" />
          <button type="submit" className="tr-btn tr-btn-primary" style={{ marginTop: '0.5rem' }} disabled={saving}>
            Add note
          </button>
        </form>
      </div>
    </div>
  );
}
